import { useEffect, useState, type ReactNode } from "react";

type FaqClusterLoadingShellProps = {
  children: ReactNode;
  itemCount: number;
  label?: string;
};

/**
 * Keeps semantic FAQ content and its page-level FAQPage source in the initial
 * HTML, while reserving the same layout with a brief client-navigation shell.
 * The shell yields on the next paint instead of waiting on a timer or fetch.
 */
export function FaqClusterLoadingShell({
  children,
  itemCount,
  label = "Loading frequently asked questions",
}: FaqClusterLoadingShellProps) {
  const [isLoading, setIsLoading] = useState(true);
  const skeletonItems = Math.max(3, Math.min(itemCount, 6));

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setIsLoading(false));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="relative" aria-busy={isLoading}>
      <noscript>
        <style>{`[data-faq-cluster-content] { opacity: 1 !important; } [data-faq-cluster-skeleton] { display: none !important; }`}</style>
      </noscript>
      <div
        data-faq-cluster-content
        className={`transition-opacity duration-150 motion-reduce:transition-none ${isLoading ? "opacity-0" : "opacity-100"}`}
      >
        {children}
      </div>

      {isLoading ? (
        <>
          <span className="sr-only" role="status" aria-live="polite">
            {label}
          </span>
          <div data-faq-cluster-skeleton className="absolute inset-0 z-10 pointer-events-none" aria-hidden="true">
          <div className="flex h-full flex-col gap-3">
            {Array.from({ length: skeletonItems }, (_, index) => (
              <div
                key={index}
                className="min-h-16 flex-1 rounded-lg border border-border/60 bg-muted/40 p-5 animate-pulse motion-reduce:animate-none"
              >
                <div className={`h-4 rounded bg-muted ${index % 3 === 2 ? "w-3/4" : "w-11/12"}`} />
              </div>
            ))}
          </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
